/**
 * Wishlist page (/apps/wishlist): flag member-exclusive cards so wishlist-page.css can hide their
 * add to cart. snippets/scripts.liquid only loads this for non-members (logged-out visitors and logged-in non-S|PLUS customers).
 *
 * The page and its `<wishlist-product-card>` elements are rendered by Swish (Wishlist King), so the
 * theme can't output the card markup and the DOM carries no product tags. The Swish window API
 * does (`item.product.tags`), so the wishlist is read with `state.observeWishlist` and
 * `data-member-exclusive` is set on the cards of items tagged `member-exclusive`.
 * Fail open: if the SDK doesn't load, nothing is flagged and the cards keep their add to cart.
 */
(() => {
  if (!location.pathname.includes('/apps/wishlist')) return;
  // Guards against this script running twice on the same page (a second run would double the
  // subscriptions and observers).
  if (window.__wishlistPageInit) return;
  window.__wishlistPageInit = true;

  const CARD_SELECTOR = 'wishlist-product-card[data-wishlist-item-id]';
  const MEMBER_EXCLUSIVE_TAG = 'member-exclusive';

  /** Ids of the wishlist items tagged member-exclusive, by wishlist id (`mine` or a shared wishlist's id). */
  const exclusiveItemIds = new Map();
  /** Wishlist ids already subscribed to. */
  const subscribed = new Set();

  /** @type {Promise<any> | undefined} */
  let appPromise;

  /** Resolves with `window.WishlistKing` as soon as the Swish SDK has loaded. */
  function getApp() {
    appPromise ??= new Promise((resolve) => {
      if (window.WishlistKing) {
        resolve(window.WishlistKing);
      } else {
        document.addEventListener('wk:app:ready', () => resolve(window.WishlistKing), { once: true });
      }
    });
    return appPromise;
  }

  function flagExclusiveCards() {
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const ids = exclusiveItemIds.get(card.getAttribute('data-wishlist-id') || 'mine');
      card.toggleAttribute('data-member-exclusive', Boolean(ids?.has(card.getAttribute('data-wishlist-item-id'))));
    });
  }

  /** @param {string} wishlistId */
  async function subscribe(wishlistId) {
    subscribed.add(wishlistId);
    try {
      const app = await getApp();
      app.state.observeWishlist({ wishlistId }).subscribe((wishlist) => {
        exclusiveItemIds.set(
          wishlistId,
          new Set(
            wishlist.items
              .filter((item) =>
                (item.product.tags || []).some((tag) => String(tag).trim().toLowerCase() === MEMBER_EXCLUSIVE_TAG)
              )
              .map((item) => item.id)
          )
        );
        flagExclusiveCards();
      });
    } catch (error) {
      console.warn('[Wishlist page] could not read the Swish wishlist state:', error);
    }
  }

  let timer;
  function scan() {
    timer = undefined;
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const wishlistId = card.getAttribute('data-wishlist-id') || 'mine';
      if (!subscribed.has(wishlistId)) subscribe(wishlistId);
    });
    flagExclusiveCards();
  }
  function queueScan() {
    if (timer) return;
    timer = setTimeout(scan, 50);
  }

  // The cards render asynchronously and Swish can recreate them, so flag them whenever the DOM changes.
  new MutationObserver(queueScan).observe(document.body, { childList: true, subtree: true });
  queueScan();
})();
